import { ResolvedSuggestion } from "../common/ObjectTypes";
import { DetailBox, suggestionOwnerKey } from "./mediaDetailComposer";
import { SpeciesOption } from "./reviewActions";

/**
 * One ticked card as the API wants it (AKL-774).
 *
 * `original_taxon_id` travels with each card because the server decides
 * accept-vs-edit per card from it, rather than re-resolving the mapping — the
 * action must commit what the reviewer was looking at.
 */
export type AssignSpeciesCardPayload = {
  prediction_ids: number[];
  original_taxon_id: number;
};

/**
 * Everything the assign-species flow needs to know about the current tick set.
 *
 * `cardCount` and `boxCount` are deliberately separate and must never be
 * conflated: every `{{count}}` in this feature's copy is the number of *cards*,
 * while the count field in the editor is prefilled from the number of *boxes*.
 * Ticking two cards covering seven boxes is "2 suggestions selected" opening an
 * editor that says 7.
 *
 * `boxCount` is the number of **distinct** boxes, not the number of predictions.
 * A card's `underlying_prediction_ids` are its label-bearing predictions, and one
 * detection can carry two crop classifications that resolve to different taxa —
 * so two ticked cards can cover a single box. Summing the id lists there would
 * prefill one animal as two, and disagree with both the highlighted boxes on the
 * image and the server's own default.
 */
export type AssignSelection = {
  cards: ResolvedSuggestion[];
  cardCount: number;
  boxCount: number;
  payload: AssignSpeciesCardPayload[];
  /**
   * The distinct taxa already sitting on the ticked cards, as picker options
   * (AKL-781).
   *
   * These are an **offer, never a preselection**. The editor still opens with
   * `taxonId: null`, even when this list has exactly one entry — see
   * `selectedSpeciesOptions` for why.
   */
  speciesOptions: SpeciesOption[];
};

/**
 * The ticked cards, **in panel order** rather than the order they were clicked.
 *
 * Panel order is what makes a request reproducible: the API processes cards in
 * the order it receives them, and its first-wins merge for a box reached by two
 * cards therefore depends on that order. Two reviewers ticking the same cards in
 * a different sequence should produce the same rows.
 *
 * Identity is `suggestionOwnerKey`, not the bare `aggregation_key`: the latter
 * repeats across the pending and rejected lists for the same species, so a bare
 * key would tick both cards and commit a cluster the reviewer never selected.
 *
 * Keys that no longer match a card are silently dropped — a card can vanish
 * between a tick and a re-fetch (another reviewer actioned it), and the
 * alternative is sending a prediction the server would reject for the whole
 * batch.
 * @param suggestions the cards currently rendered, in panel order
 * @param selectedKeys the ticked aggregation keys
 * @returns the ticked cards, in panel order
 */
export const selectedSuggestions = (
  suggestions: ResolvedSuggestion[],
  selectedKeys: Set<string>,
): ResolvedSuggestion[] =>
  suggestions.filter((s) => selectedKeys.has(suggestionOwnerKey(s)));

/**
 * The union of every ticked card's box ids, for the image highlight.
 *
 * Reads each ticked card's **own geometries**, then keeps the composed boxes
 * whose ids they name. Both halves matter:
 *
 * - Filtering the composed boxes by `ownerKey` alone does not work. A composed
 *   box has exactly one owner — `composeDetailBoxes` claims each detection for
 *   the first card that reaches it and drops the rest — so when two cards share
 *   a box and only the *later* one is ticked, an ownership filter finds nothing:
 *   no highlight, and a prefilled count of zero that the API then rejects.
 *   A card's own geometry list names the box whoever owns it.
 * - Intersecting with the composed boxes is still needed, and not only to
 *   collapse a shared box to one entry. It keeps the highlight to boxes that
 *   exist on the image, and — via `ownerKind` — stops a committed observation's
 *   box lighting up as part of a suggestion selection. `DetailBox` documents
 *   that suggestions and observations share one `ownerKey` space.
 *
 * A rejected card carries its coordinates in `rejected_geometries` rather than
 * `geometries` (the API always empties the latter for it), so both are read.
 * @param suggestions the cards currently rendered, in panel order
 * @param selectedKeys the ticked card identities
 * @param boxes the composed detail boxes
 * @returns the box ids to highlight, in box order, without duplicates
 */
export const selectedBoxIds = (
  suggestions: ResolvedSuggestion[],
  selectedKeys: Set<string>,
  boxes: DetailBox[],
): string[] => {
  const wanted = new Set<string>();
  for (const suggestion of selectedSuggestions(suggestions, selectedKeys)) {
    for (const geometry of [
      ...(suggestion.geometries ?? []),
      ...(suggestion.rejected_geometries ?? []),
    ]) {
      wanted.add(String(geometry.prediction_id));
    }
  }
  if (wanted.size === 0) return [];
  // `seen` is belt-and-braces: `composeDetailBoxes` claims each detection once,
  // so a duplicate id should not reach here. Kept because the return is a
  // highlight set and the contract above promises no duplicates.
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const box of boxes) {
    if (box.ownerKind !== "suggestion") continue;
    if (!wanted.has(box.id) || seen.has(box.id)) continue;
    seen.add(box.id);
    ids.push(box.id);
  }
  return ids;
};

/**
 * The distinct taxa already resolved across the ticked cards, as **candidate**
 * species-picker options (AKL-781).
 *
 * Candidates, not final options: `SpeciesPicker` narrows these to the ones
 * actually on the project's effective species list before offering any of them,
 * because a ticked card's taxon need not be on that list — the API composes a
 * photo's rejected cards from `(*rejected_aggregation.cards,
 * *rejected_aggregation.out_of_list_cards)` and tags every one of them
 * `"rejected"`, so an off-roster species surfaces as a tickable card, and an
 * off-roster seed is dropped rather than offered (Margaux, 2026-09-02). See
 * that component for the reasoning.
 *
 * The assign-species editor used to open with an empty picker, so a reviewer
 * ticking three "Animal" cards, two "Thomson's Gazelle" and two "Bovidae" had
 * to type "Thomson's Gazelle" from scratch — a name already on screen, on a
 * card they had just ticked. Seeding the menu with these lets them pick the
 * AI's own answer instead.
 *
 * **Deduplicated by `target_taxon_id`, first occurrence winning**, so the order
 * is panel order — which is confidence descending — and the AI's most confident
 * guess is the first option.
 *
 * The dedupe is not theoretical. One species can hold both a pending and a
 * rejected card on the same photo (AKL-774 AC 11), and pending and rejected mix
 * freely in one selection — so ticking both yields two cards with the identical
 * `target_taxon_id`, and without this the reviewer would be offered the same
 * species twice.
 *
 * The label precedence (`display_name` → `scientific_name` → `#<id>`) mirrors
 * `SpeciesPicker`'s `optionFromEffective`, so a seeded row and a searched row
 * for the same taxon read identically rather than looking like two species.
 *
 * **This never becomes a preselection**, not even when it returns exactly one
 * option (Margaux, 2026-09-02). Preselecting would turn the editor into a
 * one-click commit of a species the reviewer never chose, and the only reason
 * they are in this flow is that they judged the AI's grouping wrong. It would
 * also leave `ObservationEditor`'s `dirty` gate unsatisfied at open, disabling
 * Save until they reselected the very species it had chosen for them.
 * @param cards the ticked cards, in panel order
 * @returns one option per distinct taxon, in panel order
 */
export const selectedSpeciesOptions = (
  cards: ResolvedSuggestion[],
): SpeciesOption[] => {
  const seen = new Set<number>();
  const options: SpeciesOption[] = [];
  for (const card of cards) {
    if (seen.has(card.target_taxon_id)) continue;
    seen.add(card.target_taxon_id);
    options.push({
      value: card.target_taxon_id,
      label:
        card.display_name || card.scientific_name || `#${card.target_taxon_id}`,
      scientific: card.scientific_name || "",
    });
  }
  return options;
};

/**
 * Summarise the current tick set for the selection bar, the editor and the
 * request.
 * @param suggestions the cards currently rendered, in panel order
 * @param selectedKeys the ticked aggregation keys
 * @param boxes the composed detail boxes, for the distinct-box count
 * @returns the ticked cards, the two counts, the request payload, and the
 *   distinct taxa to seed the species picker with
 */
export const assignSelection = (
  suggestions: ResolvedSuggestion[],
  selectedKeys: Set<string>,
  boxes: DetailBox[],
): AssignSelection => {
  const cards = selectedSuggestions(suggestions, selectedKeys);
  return {
    cards,
    cardCount: cards.length,
    // Distinct boxes, taken from the same set that drives the highlight — so the
    // number in the editor is exactly the number of boxes lit up on the image,
    // and matches what the API defaults individual_count to when omitted.
    boxCount: selectedBoxIds(suggestions, selectedKeys, boxes).length,
    payload: cards.map((s) => ({
      prediction_ids: s.underlying_prediction_ids,
      original_taxon_id: s.target_taxon_id,
    })),
    speciesOptions: selectedSpeciesOptions(cards),
  };
};
